import { Badge, BlockStack, Button, Icon, InlineStack, Modal, Spinner, Text } from '@shopify/polaris';
import { CalendarIcon, ChartVerticalIcon, CheckCircleIcon, ChevronRightIcon, DataTableIcon, ProductIcon } from '@shopify/polaris-icons';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import type { StorefrontSearchReport } from '../../app/models/Reports';
import { getVisibleFitmentFields, normalizeFitmentKey } from '../../app/utils/fitmentFields';
import { FITMENT_CATALOG_LABEL_CHANGED, getCachedFitmentCatalogLabel, getStoredFitmentCatalogLabel, setCachedFitmentCatalogLabel } from '../../app/utils/fitmentCatalogLabel';
import { getSearchLabel } from '../reports/VehicleReportDetails';
import { SearchActivityChart } from '../reports/SearchActivitySummary';
import './DashboardOverview.css';

// These are distinct catalog year values, not vehicle counts or a validation verdict.
export const getUnusualVehicleYears = (years: string[], currentYear: number) =>
    Array.from(new Set(years.map(year => year.trim())))
        .filter(year => /^\d+$/.test(year) && Number(year) > currentYear + 2)
        .sort((a, b) => Number(a) - Number(b));

const count = (value: number) => value.toLocaleString();
const reportDate = (value: string) => new Date(`${value.slice(0, 10)}T00:00:00`)
    .toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const reportDays = (report?: StorefrontSearchReport) => {
    if (!report) return 30;
    // UTC dates avoid daylight-saving offsets changing the length of a period.
    const days = (Date.parse(report.to.slice(0, 10)) - Date.parse(report.from.slice(0, 10))) / 86400000 + 1;
    return Number.isFinite(days) ? Math.max(1, Math.round(days)) : 30;
};

const SearchMetric = ({ label, value, previous, compare, decimal = false }: {
    label: string; value: number; previous: number; compare: boolean; decimal?: boolean;
}) => {
    const change = previous === 0 ? (value === 0 ? 0 : null) : Math.round((value - previous) / previous * 100);
    return (
        <div className="pf-overview-stat">
            <span className="pf-overview-stat__label">{label}</span>
            <div className="pf-overview-stat__value">
                <strong>{value.toLocaleString(undefined, { maximumFractionDigits: decimal ? 1 : 0 })}</strong>
                {compare && <span className="pf-overview-stat__change" aria-label={change === null
                    ? 'New compared with the previous period'
                    : `${Math.abs(change)} percent ${change < 0 ? 'decrease' : 'increase'} from the previous period`}>
                    {change === null ? 'New' : `${change > 0 ? '+' : ''}${change}%`}
                </span>}
            </div>
        </div>
    );
};

export default function DashboardOverview() {
    const navigate = useNavigate();
    const [report, setReport] = useState<StorefrontSearchReport>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [reload, setReload] = useState(0);
    const [unusualYears, setUnusualYears] = useState<string[]>([]);
    const [checkLoading, setCheckLoading] = useState(true);
    const [checkError, setCheckError] = useState(false);
    const [checkReload, setCheckReload] = useState(0);
    const [yearFieldAvailable, setYearFieldAvailable] = useState(false);
    const [checkedYearCount, setCheckedYearCount] = useState(0);
    const [yearReviewOpen, setYearReviewOpen] = useState(false);
    const [vehicleCatalogLabel, setVehicleCatalogLabel] = useState(() => getStoredFitmentCatalogLabel() ?? '');
    const yearReviewActivator = useRef<HTMLDivElement>(null);
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        const handleLabelChange = (event: Event) => {
            const label = (event as CustomEvent<string>).detail;
            if (label) setVehicleCatalogLabel(label);
        };
        window.addEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
        return () => window.removeEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(undefined);
        const load = async () => {
            try {
                const result = await agent.Reports.searchActivity({ days: 30, completedDaysOnly: true, limit: 5 });
                if (!cancelled) setReport(result);
            } catch (failure) {
                const message = await getApiErrorMessage(failure, 'Unable to load search activity.');
                if (!cancelled) setError(message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [reload]);

    useEffect(() => {
        const controller = new AbortController();
        setCheckLoading(true);
        setCheckError(false);
        const load = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (controller.signal.aborted) return;
                setVehicleCatalogLabel(setCachedFitmentCatalogLabel(catalog.name));
                const keys = getVisibleFitmentFields(catalog).map(normalizeFitmentKey);
                setYearFieldAvailable(keys.includes('year'));
                if (!keys.includes('year')) {
                    setUnusualYears([]);
                    setCheckedYearCount(0);
                    return;
                }
                // Request just the year facet, first in cascade order, across the full catalog.
                const result = await agent.Vehicles.filterOptions('', '', '', {},
                    ['year', ...keys.filter(key => key !== 'year')], ['year'], controller.signal);
                if (!controller.signal.aborted) {
                    const years = result.optionsByField?.year ?? result.years ?? [];
                    setCheckedYearCount(new Set(years.map(year => year.trim()).filter(year => /^\d+$/.test(year))).size);
                    setUnusualYears(getUnusualVehicleYears(years, currentYear));
                }
            } catch {
                if (!controller.signal.aborted) {
                    setVehicleCatalogLabel(getCachedFitmentCatalogLabel());
                    setCheckError(true);
                }
            } finally {
                if (!controller.signal.aborted) setCheckLoading(false);
            }
        };
        void load();
        return () => controller.abort();
    }, [currentYear, checkReload]);

    const days = reportDays(report);
    const total = report?.totalSearches ?? 0;
    const top = (Array.isArray(report?.topSearches) ? report?.topSearches ?? [] : []).filter(Boolean).slice(0, 5);
    const otherSearches = Math.max(0, total - top.reduce((sum, row) => sum + row.searchCount, 0));
    const otherVehicles = Math.max(0, (report?.uniqueSearchCount ?? 0) - top.length);
    const compare = Boolean(report?.comparisonEnabled && report.dataAvailableFrom && report.previousFrom
        && report.previousFrom.slice(0, 10) >= report.dataAvailableFrom.slice(0, 10));
    const checkStatus = checkLoading ? 'Checking years…' : checkError ? 'Check unavailable'
        : !yearFieldAvailable ? 'No visible year field in this catalog'
        : checkedYearCount === 0 ? 'No numeric vehicle years to check'
        : unusualYears.length > 0 ? `${count(unusualYears.length)} unusual ${unusualYears.length === 1 ? 'year' : 'years'}`
        : 'No unusual future years found';
    const checkAction = checkError ? 'Retry' : unusualYears.length > 0 ? 'Review' : 'View check';
    const reportUrl = (kind?: string) => {
        const params = new URLSearchParams();
        if (kind) params.set('report', kind);
        if (report) {
            params.set('from', report.from.slice(0, 10));
            params.set('to', report.to.slice(0, 10));
        }
        const query = params.toString();
        return `/reports${query ? `?${query}` : ''}`;
    };

    return (
        <div className="pf-overview">
            <div className="pf-overview-actions">
                <section className="pf-overview-action-card" aria-labelledby="pf-overview-catalog-title">
                    <header className="pf-overview-action-heading">
                        <Text as="h2" id="pf-overview-catalog-title" variant="headingSm">Fitment catalog</Text>
                        <Text as="p" variant="bodySm" tone="subdued">Keep product compatibility and vehicle data up to date.</Text>
                    </header>
                    {[{ label: 'Product fitment', detail: 'Review coverage and compatibility', path: '/products', icon: ProductIcon },
                        { label: vehicleCatalogLabel || '\u00a0', detail: 'Add, edit, and organize vehicles', path: '/vehicles', icon: DataTableIcon }].map(action => (
                        <button key={action.path} type="button" className="pf-overview-shortcut" onClick={() => navigate(action.path)}>
                            <Icon source={action.icon} /><span className="pf-overview-shortcut-copy"><strong>{action.label}</strong><small>{action.detail}</small></span><Icon source={ChevronRightIcon} tone="subdued" />
                        </button>
                    ))}
                </section>
                <section className="pf-overview-action-card" aria-labelledby="pf-overview-matching-title">
                    <header className="pf-overview-action-heading">
                        <Text as="h2" id="pf-overview-matching-title" variant="headingSm">Catalog insights</Text>
                        <Text as="p" variant="bodySm" tone="subdued">Find coverage gaps and review your catalog’s next steps.</Text>
                    </header>
                    <button type="button" className="pf-overview-shortcut" onClick={() => navigate(reportUrl('coverage_opportunities'))}>
                        <Icon source={ChartVerticalIcon} /><span className="pf-overview-shortcut-copy"><strong>Coverage opportunities</strong><small>Find searches with few matching products</small></span><Icon source={ChevronRightIcon} tone="subdued" />
                    </button>
                    <div ref={yearReviewActivator} className="pf-overview-check-activator">
                        <button type="button" className="pf-overview-shortcut" disabled={checkLoading}
                            aria-label={`Vehicle year check: ${checkStatus}${checkLoading ? '' : `. ${checkAction}`}`}
                            onClick={() => checkError ? setCheckReload(value => value + 1) : setYearReviewOpen(true)}>
                            <Icon source={!checkLoading && !checkError && checkedYearCount > 0 && unusualYears.length === 0 ? CheckCircleIcon : CalendarIcon} />
                            <span className="pf-overview-shortcut-copy"><strong>Vehicle year check</strong>
                                <span className="pf-overview-check-status" aria-live="polite">
                                    {!checkLoading && !checkError && unusualYears.length > 0 ? <Badge tone="attention">{checkStatus}</Badge> : checkStatus}
                                </span>
                            </span>
                            {!checkLoading && <span className="pf-overview-shortcut-action">{checkAction}</span>}
                        </button>
                    </div>
                </section>
            </div>
            <section className="pf-overview-activity" aria-label="Storefront search activity">
                <header className="pf-overview-activity-heading">
                    <BlockStack gap="100"><Text as="h2" variant="headingMd">Storefront search activity</Text>
                        <Text as="p" variant="bodySm" tone="subdued">Last {days} complete days{report ? ` · ${reportDate(report.from)} – ${reportDate(report.to)}` : ''}</Text>
                    </BlockStack>
                    <Button onClick={() => navigate(reportUrl())}>View reports</Button>
                </header>
            {loading ? <div className="pf-overview-state" role="status">
                <Spinner size="small" accessibilityLabel="Loading search activity" /><Text as="p" tone="subdued">Loading search activity…</Text>
            </div> : error ? <div className="pf-overview-state"><BlockStack gap="200">
                <Text as="h2" variant="headingSm">Search activity is temporarily unavailable</Text>
                <Text as="p" tone="subdued">{error}</Text>
                <InlineStack><Button onClick={() => setReload(value => value + 1)}>Try again</Button></InlineStack>
            </BlockStack></div> : <div className="pf-overview-metrics">
                <SearchMetric label="Total searches" value={total}
                    previous={report?.previousTotalSearches ?? 0} compare={compare} />
                <SearchMetric label="Unique vehicles searched" value={report?.uniqueSearchCount ?? 0}
                    previous={report?.previousUniqueSearchCount ?? 0} compare={compare} />
                <SearchMetric label="Average searches / day" value={total / days} decimal
                    previous={(report?.previousTotalSearches ?? 0) / days} compare={compare} />
            </div>}

            {!loading && !error && <div className="pf-overview-analysis">
                <section className="pf-overview-trend" aria-labelledby="pf-overview-trend-title">
                    <header className="pf-overview-panel-heading">
                        <Text as="h3" variant="headingSm" id="pf-overview-trend-title">Daily search volume</Text>
                        <Text as="p" variant="bodySm" tone="subdued">Most recent 7 complete days</Text>
                    </header>
                    <SearchActivityChart report={report} days={7} compact embedded variant="line" />
                </section>
                <section className="pf-overview-demand" aria-labelledby="pf-overview-demand-title">
                    <header className="pf-overview-panel-heading">
                        <Text as="h3" variant="headingSm" id="pf-overview-demand-title">Top vehicle searches</Text>
                        <Text as="p" variant="bodySm" tone="subdued">Ranked over the last {days} complete days</Text>
                    </header>
                    {top.length === 0 ? <div className="pf-overview-state">
                        <Text as="p" tone="subdued">No vehicle searches were recorded in this range.</Text>
                    </div> : <>
                        <div className="pf-overview-list-heading" aria-hidden="true"><span>Vehicle</span><span>Searches / share</span></div>
                        <ul className="pf-overview-searches">
                            {top.map((search, index) => {
                                const label = getSearchLabel(search);
                                const share = total > 0 ? Math.min(100, search.searchCount / total * 100) : 0;
                                const fields = Object.fromEntries(Object.entries(search.fieldValues ?? {})
                                    .filter(([key, value]) => key.trim() && value?.trim()));
                                const productPath = `/products?${new URLSearchParams({ fieldValues: JSON.stringify(fields) })}`;
                                return <li key={`${label}-${index}`}>
                                    <details className="pf-overview-search">
                                        <summary aria-label={`${label}: ${count(search.searchCount)} searches, ${Math.round(share)} percent of searches`}>
                                            <span className="pf-overview-search__summary"><strong>{label}</strong>
                                                <span className="pf-overview-search__count">{count(search.searchCount)}<small>{Math.round(share)}%</small></span>
                                            </span>
                                            <span className="pf-overview-search__track" aria-hidden="true"><span style={{ width: `${share}%` }} /></span>
                                        </summary>
                                        <div className="pf-overview-search__detail"><Text as="p" variant="bodySm" tone="subdued">
                                            {count(search.searchCount)} of {count(total)} searches in this period.
                                        </Text>{Object.keys(fields).length > 0 && <Button variant="plain" onClick={() => navigate(productPath)}>
                                            View matching products
                                        </Button>}</div>
                                    </details>
                                </li>;
                            })}
                        </ul>
                        {otherVehicles > 0 && <div className="pf-overview-other">
                            <span>{count(otherVehicles)} other {otherVehicles === 1 ? 'vehicle' : 'vehicles'}</span>
                            <span>{count(otherSearches)} searches</span>
                        </div>}
                    </>}
                </section>
            </div>}
                {!loading && !error && <footer className="pf-overview-activity-footer">
                    <Text as="span" variant="bodySm" tone="subdued">{top.length > 0 && total > 0
                        ? `${getSearchLabel(top[0])} accounts for ${Math.round(top[0].searchCount / total * 100)}% of searches.`
                        : 'Review demand and fitment coverage in Reports.'}</Text>
                    <Button variant="plain" onClick={() => navigate(reportUrl('coverage_opportunities'))}>Review coverage</Button>
                </footer>}
            </section>
            <Modal open={yearReviewOpen} activator={yearReviewActivator} onClose={() => setYearReviewOpen(false)} title="Vehicle year check"
                primaryAction={{ content: 'Done', onAction: () => setYearReviewOpen(false) }}>
                <Modal.Section>
                    <BlockStack gap="300">
                        <Text as="p" tone="subdued">
                            {checkStatus}.
                        </Text>
                        <Text as="p" tone="subdued">{!yearFieldAvailable
                            ? 'This catalog has no enabled, visible year field, so the year check does not apply.'
                            : `${count(checkedYearCount)} distinct numeric year values checked. Years up to ${currentYear + 2} pass this check; values after ${currentYear + 2} are flagged for review. This does not validate complete vehicle combinations.`}</Text>
                        {unusualYears.length > 0 && <ul className="pf-overview-year-review" aria-label="Unusual vehicle years">
                            {unusualYears.map(year => <li key={year}>
                                <Text as="span" fontWeight="medium">{year}</Text>
                                <Button variant="plain" accessibilityLabel={`Review vehicles for year ${year}`} onClick={() => {
                                    setYearReviewOpen(false);
                                    navigate(`/vehicles?${new URLSearchParams({ year })}`);
                                }}>Review vehicles</Button>
                            </li>)}
                        </ul>}
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </div>
    );
}
